import React, { useEffect, useState } from "react";
import "./LeaveApproveList.scss";
import { DatePicker, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { getAllLeavesAPI } from "../../../Redux/Apis/leaveApi";
import {
  setLeaveApproveCurrentPage,
  setLeaveApproveMonthFilter,
  setLeaveApproveSearchFilter,
  setLeaveApproveStatusFilter,
} from "../../../Redux/Slices/leaveApproveSlice";
import LeaveStatusFilter from "../LeaveStatusFilter/LeaveStatusFilter";
import Loader from "../../atoms/Loader/Loader";
import EditIcon from "../../../assets/icon/edit.png";
import { LeaveApproverDetailsDrawer } from "../LeaveApproverDetailsDrawer/LeaveApproverDetailsDrawer";
import {
  approverLeaveStatusBadgeClass,
  formatApproverLeaveStatusLabel,
} from "../../../app/utils/common/leaveStatusDisplay";
import { isWfhLeaveRow } from "../../../app/utils/common/Functions";
import { DEFAULT_PROFILE_IMAGE } from "../../../app/utils/common/baseUrl";

function leaveTypeHighlightClass(leave) {
  if (isWfhLeaveRow(leave)) {
    return "bg-teal-50 text-teal-900 ring-1 ring-inset ring-teal-200";
  }
  const t = String(leave?.leaveType || "").trim();
  if (t === "Early Leave") {
    return "bg-amber-50 text-amber-900 ring-1 ring-inset ring-amber-200";
  }
  if (t === "Half Leave") {
    return "bg-violet-50 text-violet-900 ring-1 ring-inset ring-violet-200";
  }
  if (t === "Full Leave") {
    return "bg-sky-50 text-sky-900 ring-1 ring-inset ring-sky-200";
  }
  if (t === "Multiple Days Leave") {
    return "bg-emerald-50 text-emerald-900 ring-1 ring-inset ring-emerald-200";
  }
  return "bg-slate-100 text-slate-800 ring-1 ring-inset ring-slate-200";
}

function formatLeaveTypeLabel(leave) {
  if (isWfhLeaveRow(leave)) return "WFH";
  return leave?.leaveType || "—";
}

const formatLeaveDateTime = (date, time) => {
  if (date && time) {
    return new Date(`${date.split("T")[0]}T${time}`).toLocaleString();
  }
  if (date) return new Date(date).toLocaleDateString();
  if (time) return new Date(`1970-01-01T${time}`).toLocaleTimeString();
  return "-";
};

const leaveCalendarDay = (date) => {
  if (date == null || date === "") return null;
  const s = String(date).trim();
  const datePart = s.includes("T") ? s.split("T")[0] : s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const isSingleDayLeave = (leave) => {
  const start = leaveCalendarDay(leave?.startDate);
  const end = leaveCalendarDay(leave?.endDate) || start;
  return Boolean(start && end && start === end);
};

export const LeaveApproveList = () => {
  const dispatch = useDispatch();
  const {
    allLeaves,
    pagination,
    loading,
    isAllLeaveUpdated,
    statusFilter,
    searchFilter,
    monthFilter,
  } = useSelector((state) => state.leaveAprrove);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(searchFilter || "");

  const fetchApproverLeaves = () => {
    dispatch(
      getAllLeavesAPI({
        page: pagination.currentPage,
        limit: pagination.pageSize,
        status: statusFilter || undefined,
        search: searchFilter || undefined,
        month: monthFilter || undefined,
      })
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const nextSearch = searchInput.trim();
      if (nextSearch !== (searchFilter || "")) {
        dispatch(setLeaveApproveSearchFilter(nextSearch));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [dispatch, searchInput, searchFilter]);

  useEffect(() => {
    fetchApproverLeaves();
  }, [
    dispatch,
    pagination.currentPage,
    pagination.pageSize,
    statusFilter,
    searchFilter,
    monthFilter,
  ]);

  useEffect(() => {
    if (isAllLeaveUpdated) {
      fetchApproverLeaves();
    }
  }, [dispatch, isAllLeaveUpdated]);

  const handlePageChange = (newPage) => {
    dispatch(setLeaveApproveCurrentPage(newPage));
  };

  const handleMonthChange = (value) => {
    dispatch(setLeaveApproveMonthFilter(value ? value.format("YYYY-MM") : ""));
  };

  const openLeaveDetailsDrawer = (leave) => {
    setSelectedLeave(leave);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedLeave(null);
  };

  const getCancelledReason = (leave) => {
    if (String(leave?.status || "").toLowerCase() !== "cancelled") return "";
    const cancellationComment = String(leave?.cancellationComment || "").trim();
    if (cancellationComment) return cancellationComment;
    const latestActionComment = String(leave?.latestActionComment || "").trim();
    if (latestActionComment) return latestActionComment;
    const approvers = Array.isArray(leave?.approvers) ? leave.approvers : [];
    const cancelledEntry = [...approvers]
      .reverse()
      .find((a) => String(a?.status || "").toLowerCase() === "cancelled");
    return String(cancelledEntry?.comment || "").trim();
  };

  const getReasonStatusNotes = (leave) => {
    const main = String(leave?.reason || "").trim();
    const notes = [];
    const st = String(leave?.status || "").toLowerCase();
    const crs = String(leave?.cancelRequestStatus || "none").toLowerCase();
    const approvers = Array.isArray(leave?.approvers) ? leave.approvers : [];

    const pushIf = (label, text) => {
      const t = String(text || "").trim();
      if (!t || t === main) return;
      notes.push({ label, text: t });
    };

    if (st === "cancelled") {
      pushIf("Cancellation", getCancelledReason(leave));
    }
    if (st === "approved" && crs === "pending") {
      pushIf("Cancellation request", leave?.cancelRequestComment);
    }
    if (st === "rejected") {
      const rejectedEntry = [...approvers]
        .reverse()
        .find((a) => String(a?.status || "").toLowerCase() === "rejected");
      pushIf("Rejection", rejectedEntry?.comment);
    }
    if (st === "pending" && leave?.managerStatus) {
      const ms = String(leave.managerStatus).toLowerCase();
      const mgrEntry = [...approvers]
        .reverse()
        .find((a) => String(a?.status || "").toLowerCase() === ms);
      const mgrName =
        `${leave.managerActionFirstname || ""} ${leave.managerActionLastname || ""}`.trim();
      const label = mgrName ? `Manager (${ms}) — ${mgrName}` : `Manager (${ms})`;
      pushIf(label, mgrEntry?.comment);
    }

    return notes;
  };

  const renderReasonContent = (leave) => {
    const notes = getReasonStatusNotes(leave);
    return (
      <>
        <p className="leave-approve-card__reason-main" title={leave?.reason || ""}>
          {String(leave?.reason || "").trim() || "—"}
        </p>
        {notes.length > 0 ? (
          <div className="leave-approve-card__reason-notes">
            {notes.map((n, i) => (
              <p key={i} className="leave-approve-card__reason-note" title={n.text}>
                <span className="font-semibold text-gray-700">{n.label}:</span> {n.text}
              </p>
            ))}
          </div>
        ) : null}
      </>
    );
  };

  const renderPaginationButtons = () => {
    const buttons = [];

    buttons.push(
      <button
        key="prev"
        type="button"
        onClick={() => handlePageChange(pagination.currentPage - 1)}
        disabled={pagination.currentPage === 1}
        className={`px-3 py-1 rounded-md ${pagination.currentPage === 1
          ? "bg-[#e7ecf8] text-[#94a0b8] cursor-not-allowed"
          : "bg-btn-primary text-white "
          }`}
      >
        Previous
      </button>
    );

    for (let i = 1; i <= pagination.totalPages; i++) {
      buttons.push(
        <button
          key={i}
          type="button"
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-md ${pagination.currentPage === i
            ? "bg-btn-primary text-white"
            : "bg-[#eef2fc] text-[#687895]"
            }`}
        >
          {i}
        </button>
      );
    }

    buttons.push(
      <button
        key="next"
        type="button"
        onClick={() => handlePageChange(pagination.currentPage + 1)}
        disabled={pagination.currentPage === pagination.totalPages}
        className={`px-3 py-1 rounded-md ${pagination.currentPage === pagination.totalPages
          ? "bg-[#e7ecf8] text-[#94a0b8] cursor-not-allowed"
          : "bg-btn-primary text-white "
          }`}
      >
        Next
      </button>
    );

    return buttons;
  };

  const formatEmployeeName = (leave) =>
    `${leave?.userFirstname || ""} ${leave?.userLastname || ""}`.trim() || "—";

  const renderEmployeeAvatar = (leave, className = "leave-approve-card__avatar") => {
    const name = formatEmployeeName(leave);
    const imageSrc = leave?.userProfileImage || DEFAULT_PROFILE_IMAGE;

    return (
      <div className={className} aria-hidden={name === "—"}>
        <img
          src={imageSrc}
          alt={name !== "—" ? `${name} profile` : ""}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = DEFAULT_PROFILE_IMAGE;
          }}
        />
      </div>
    );
  };

  const getRowNumber = (index) =>
    (pagination.currentPage - 1) * pagination.pageSize + index + 1;

  const renderLeaveCard = (leave, index) => (
    <article
      key={leave?.leaveRequestId ?? index}
      className="leave-approve-card leave-management-card"
    >
      <header className="leave-approve-card__header leave-management-card__header">
        <div className="leave-approve-card__header-top">
          <span className="leave-approve-card__index">{getRowNumber(index)}</span>
          <span
            className={`leave-approve-card__type ${leaveTypeHighlightClass(leave)}`}
            title={formatLeaveTypeLabel(leave)}
          >
            {formatLeaveTypeLabel(leave)}
          </span>
          <span
            className={`leave-approve-card__status ${approverLeaveStatusBadgeClass(leave)}`}
          >
            {formatApproverLeaveStatusLabel(leave)}
          </span>
        </div>
        <div className="leave-approve-card__employee-row">
          {renderEmployeeAvatar(leave)}
          <p className="leave-approve-card__employee">{formatEmployeeName(leave)}</p>
        </div>
      </header>

      <dl className="leave-approve-card__details leave-management-card__details">
        <div className="leave-approve-card__detail leave-approve-card__detail--full leave-management-detail--highlight">
          <dt>Category</dt>
          <dd>
            <span className="leave-management-category-badge">
              {isWfhLeaveRow(leave) ? "Work from home" : leave?.leaveCategory || "—"}
            </span>
          </dd>
        </div>
        {isWfhLeaveRow(leave) ? (
          <div className="leave-approve-card__detail leave-approve-card__detail--full">
            <dt>Work location</dt>
            <dd>
              <span className="leave-management-category-badge bg-teal-50 text-teal-900">
                WFH
              </span>
            </dd>
          </div>
        ) : null}
        <div className="leave-approve-card__detail leave-approve-card__detail--employee-desktop">
          <dt>Employee</dt>
          <dd className="leave-approve-card__employee-cell">
            {renderEmployeeAvatar(leave, "leave-approve-card__avatar leave-approve-card__avatar--sm")}
            <span>{formatEmployeeName(leave)}</span>
          </dd>
        </div>
        <div className="leave-approve-card__detail leave-approve-card__detail--dates leave-approve-card__detail--full leave-management-detail--highlight">
          <dt>Period</dt>
          {isSingleDayLeave(leave) ? (
            <dd className="leave-management-period-boxes leave-management-period-boxes--single">
              <div className="leave-management-date-box">
                <span className="leave-management-date-box__label">Date</span>
                <span className="leave-management-date-box__value">
                  {formatLeaveDateTime(leave?.startDate)}
                </span>
                {leave?.startTime || leave?.endTime ? (
                  <span className="leave-management-date-box__time">
                    {[leave?.startTime, leave?.endTime].filter(Boolean).join(" – ")}
                  </span>
                ) : null}
              </div>
            </dd>
          ) : (
            <dd className="leave-management-period-boxes">
              <div className="leave-management-date-box">
                <span className="leave-management-date-box__label">From</span>
                <span className="leave-management-date-box__value">
                  {formatLeaveDateTime(leave?.startDate, leave?.startTime)}
                </span>
              </div>
              <div className="leave-management-date-box">
                <span className="leave-management-date-box__label">To</span>
                <span className="leave-management-date-box__value">
                  {formatLeaveDateTime(leave?.endDate, leave?.endTime)}
                </span>
              </div>
            </dd>
          )}
        </div>
        <div className="leave-approve-card__detail leave-approve-card__detail--full leave-management-detail--highlight">
          <dt>Reason</dt>
          <dd>{renderReasonContent(leave)}</dd>
        </div>
      </dl>

      <footer className="leave-approve-card__actions leave-management-card__actions">
        <button
          type="button"
          className="leave-approve-card__review-btn"
          onClick={() => openLeaveDetailsDrawer(leave)}
        >
          <img src={EditIcon} alt="" />
          Review request
        </button>
      </footer>
    </article>
  );

  return (
    <div>
      <div className="leave-approve-list-container leave-management-list-panel card-border card-body mb-4">
        <div className="leave-management-list-panel__head">
          <h2 className="leave-management-list-panel__title">Leave requests</h2>
          <span className="leave-management-list-panel__count">
            {pagination.totalData} total
          </span>
        </div>

        <div className="leave-approve-list__filters">
          <Input
            allowClear
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search employee, email, type, reason…"
            prefix={<SearchOutlined className="text-[#94a0b8]" />}
            className="leave-approve-list__search"
            aria-label="Search leave requests"
          />
          <DatePicker
            picker="month"
            allowClear
            value={monthFilter ? moment(monthFilter, "YYYY-MM") : null}
            onChange={handleMonthChange}
            placeholder="Filter by month"
            className="leave-approve-list__month"
            format="MMM YYYY"
          />
        </div>

        <LeaveStatusFilter
          value={statusFilter}
          onChange={(next) => dispatch(setLeaveApproveStatusFilter(next))}
          className="leave-approve-list__status-filter"
        />

        {loading ? (
          <Loader />
        ) : (
          <>
            {allLeaves.length === 0 ? (
              <div className="leave-management-empty">
                <p className="leave-management-empty__title">
                  {searchFilter || monthFilter || statusFilter
                    ? "No matching leave requests"
                    : "No leave requests to review"}
                </p>
                <p className="leave-management-empty__text">
                  {searchFilter || monthFilter || statusFilter
                    ? "Try a different search, month, or status filter."
                    : "When employees submit leave or WFH requests, they will show up here for approval. HR-assigned WFH entries also appear here as approved."}
                </p>
              </div>
            ) : (
              <div
                className="leave-approve-list-cards leave-management-cards"
                aria-label="Leave approval requests"
              >
                {allLeaves.map((leave, index) => renderLeaveCard(leave, index))}
              </div>
            )}

            {allLeaves.length > 0 ? (
              <div className="leave-approve-list-pagination leave-management-pagination">
                <div className="leave-management-pagination__meta">
                  Showing {allLeaves.length} of {pagination.totalData} entries
                </div>
                {pagination.totalPages > 1 ? (
                  <div className="leave-management-pagination__buttons">
                    {renderPaginationButtons()}
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>

      {drawerOpen ? (
        <LeaveApproverDetailsDrawer leave={selectedLeave} onClose={closeDrawer} />
      ) : null}
    </div>
  );
};
